import { useEffect, useState } from "react";
import { GetServerSidePropsContext } from "next";
import { useRouter } from "next/router";
import LoadingAnimation from "@/atoms/LoadingAnimation";
import MembershipStepFlow from "components/Membership/MembershipStepFlow";
import { MembershipStep } from "components/Membership/membershipTypes";
import { getServerSidePropsMembership } from "components/Membership/membershipUtil";
import useMembershipSession from "components/Membership/useMembershipSession";
import {
  getServerSideRedirectOptionsSubscribeFlow,
  getSubscribeSteps,
  shouldOmitBudgetStepForAppliedFall25Coupon,
} from "components/Subscribe/subscribeUtil";
import useExcludeFromAnnual from "utils/hooks/useExcludeFromAnnual";

type SubscribeServerSideProps = ServerSideProps<typeof getServerSideProps>;

export const Page = (props: SubscribeServerSideProps) => {
  const [steps, setSteps] = useState<MembershipStep[] | null>(null);
  const router = useRouter();

  const { excludeFromAnnual, isAffiliateVisitor } = useExcludeFromAnnual();

  const { checkoutSession, isAuthenticated, orderSummary } =
    useMembershipSession({
      checkoutSession: props.checkoutSession,
      isAuthenticated: props.isAuthenticated,
      orderSummary: props.orderSummary,
    });

  // See membership/[slug]: force step list refresh when extCouponCode is added without full navigation.
  const extCouponQueryKey = router.isReady
    ? String(
        Array.isArray(router.query.extCouponCode)
          ? router.query.extCouponCode[0] ?? ""
          : router.query.extCouponCode ?? ""
      )
    : "";

  const omitBudgetStep = shouldOmitBudgetStepForAppliedFall25Coupon({
    checkoutSession,
    plans: props.plans,
    routerQuery: router.isReady ? router.query : undefined,
  });

  useEffect(() => {
    const _steps = getSubscribeSteps({
      checkoutSession,
      excludeFromAnnual,
      isAffiliateVisitor,
      isAuthenticated,
      omitBudgetStep,
    });
    setSteps(_steps);
  }, [
    checkoutSession,
    excludeFromAnnual,
    isAffiliateVisitor,
    isAuthenticated,
    omitBudgetStep,
    extCouponQueryKey, // same as membership: URL-only Fall25 claim must refresh steps
  ]);

  useEffect(() => {
    window.dataLayer?.push({
      event: "funnelentry",
      eventCategory: "KidsSubscription",
    });
  }, []);

  return !checkoutSession || !steps ? (
    <LoadingAnimation message="Loading..." />
  ) : (
    <MembershipStepFlow
      {...props}
      checkoutSession={checkoutSession}
      excludeFromAnnual={excludeFromAnnual}
      isAuthenticated={isAuthenticated}
      membershipSteps={steps}
      orderSummary={orderSummary}
    />
  );
};

export const getServerSideProps = async (ctx: GetServerSidePropsContext) => {
  const props = await getServerSidePropsMembership(ctx);

  const { checkoutSession, invoice, isAuthenticated } = props;

  const { shouldRedirect, redirect } =
    getServerSideRedirectOptionsSubscribeFlow({
      checkoutSession,
      ctx,
      invoice,
      isAuthenticated: Boolean(isAuthenticated),
    });

  if (shouldRedirect) {
    return { redirect };
  }

  return { props };
};

export default Page;
